<script lang="ts">
	import { _ } from 'svelte-i18n';
	import Header from '$lib/components/sections/Header.svelte';
	import Footer from '$lib/components/sections/Footer.svelte';
	import Icon from '$lib/components/primitives/Icon.svelte';
	
	interface SitemapSection {
		title: string;
		icon: string;
		links: {
			label: string;
			href: string;
			description?: string;
		}[];
	}
	
	const sections: SitemapSection[] = [
		{
			title: $_('pages.sitemap.sections.main.title'),
			icon: 'home',
			links: [
				{ label: $_('pages.sitemap.sections.main.home'), href: '/', description: $_('pages.sitemap.sections.main.homeDesc') },
				{ label: $_('pages.sitemap.sections.main.pricing'), href: '/pricing', description: $_('pages.sitemap.sections.main.pricingDesc') },
				{ label: $_('pages.sitemap.sections.main.blog'), href: '/blog', description: $_('pages.sitemap.sections.main.blogDesc') }
			]
		},
		{
			title: $_('pages.sitemap.sections.product.title'),
			icon: 'layers',
			links: [
				{ label: $_('pages.sitemap.sections.product.features'), href: '/#features', description: $_('pages.sitemap.sections.product.featuresDesc') },
				{ label: $_('pages.sitemap.sections.product.integrations'), href: '/integrations', description: $_('pages.sitemap.sections.product.integrationsDesc') },
				{ label: $_('pages.sitemap.sections.product.howItWorks'), href: '/#how-it-works', description: $_('pages.sitemap.sections.product.howItWorksDesc') },
				{ label: $_('pages.sitemap.sections.product.testimonials'), href: '/#testimonials', description: $_('pages.sitemap.sections.product.testimonialsDesc') }
			]
		},
		{
			title: $_('pages.sitemap.sections.resources.title'),
			icon: 'book',
			links: [
				{ label: $_('pages.sitemap.sections.resources.gettingStarted'), href: '/getting-started', description: $_('pages.sitemap.sections.resources.gettingStartedDesc') },
				{ label: $_('pages.sitemap.sections.resources.documentation'), href: '/docs', description: $_('pages.sitemap.sections.resources.documentationDesc') },
				{ label: $_('pages.sitemap.sections.resources.helpCenter'), href: '/docs/contact-support', description: $_('pages.sitemap.sections.resources.helpCenterDesc') }
			]
		},
		{
			title: $_('pages.sitemap.sections.company.title'),
			icon: 'building',
			links: [
				{ label: $_('pages.sitemap.sections.company.about'), href: '/#about', description: $_('pages.sitemap.sections.company.aboutDesc') },
				{ label: $_('pages.sitemap.sections.company.contact'), href: '/#contact', description: $_('pages.sitemap.sections.company.contactDesc') },
				{ label: $_('pages.sitemap.sections.company.changelog'), href: '#changelog', description: $_('pages.sitemap.sections.company.changelogDesc') }
			]
		},
		{
			title: $_('pages.sitemap.sections.legal.title'),
			icon: 'shield',
			links: [
				{ label: $_('pages.sitemap.sections.legal.privacy'), href: '/privacy-policy', description: $_('pages.sitemap.sections.legal.privacyDesc') },
				{ label: $_('pages.sitemap.sections.legal.terms'), href: '/terms-of-use', description: $_('pages.sitemap.sections.legal.termsDesc') },
				{ label: $_('pages.sitemap.sections.legal.cookies'), href: '/privacy-policy#cookies', description: $_('pages.sitemap.sections.legal.cookiesDesc') }
			]
		},
		{
			title: $_('pages.sitemap.sections.connect.title'),
			icon: 'users',
			links: [
				{ label: $_('pages.sitemap.sections.connect.twitter'), href: 'https://twitter.com/heyweekapp', description: $_('pages.sitemap.sections.connect.twitterDesc') },
				{ label: $_('pages.sitemap.sections.connect.linkedin'), href: 'https://www.linkedin.com/company/heyweek/', description: $_('pages.sitemap.sections.connect.linkedinDesc') }
			]
		}
	];
</script>

<svelte:head>
	<title>{$_('pages.sitemap.meta.title')} - Heyweek</title>
	<meta name="description" content={$_('pages.sitemap.meta.description')} />
	<meta property="og:title" content="{$_('pages.sitemap.meta.title')} - Heyweek" />
	<meta property="og:description" content={$_('pages.sitemap.meta.description')} />
</svelte:head>

<Header />

<main class="sitemap-page">
	<div class="container">
		<div class="sitemap-header">
			<h1 class="sitemap-title">{$_('pages.sitemap.title')}</h1>
			<p class="sitemap-description">
				{$_('pages.sitemap.description')}
			</p>
		</div>
		
		<div class="sitemap-grid">
			{#each sections as section}
				<div class="sitemap-section">
					<div class="section-header">
						<div class="section-icon">
							<Icon name={section.icon} size={20} />
						</div>
						<h2 class="section-title">{section.title}</h2>
					</div>
					<ul class="section-links">
						{#each section.links as link}
							<li class="link-item">
								<a 
									href={link.href}
									class="link-url"
									target={link.href.startsWith('http') ? '_blank' : undefined}
									rel={link.href.startsWith('http') ? 'noopener noreferrer' : undefined}
								>
									{link.label}
									{#if link.href.startsWith('http')}
										<Icon name="external-link" size={14} />
									{/if}
								</a>
								{#if link.description}
									<p class="link-description">{link.description}</p>
								{/if}
							</li>
						{/each}
					</ul>
				</div>
			{/each}
		</div>
		
		<div class="sitemap-footer">
			<div class="xml-sitemap">
				<Icon name="file-text" size={20} />
				<p>{$_('pages.sitemap.xmlSitemap.text')} <a href="/sitemap.xml">{$_('pages.sitemap.xmlSitemap.link')}</a></p>
			</div>
		</div>
	</div>
</main>

<Footer />

<style>
	.sitemap-page {
		padding-block: var(--space-16);
		background: var(--bg-primary);
		min-height: 100vh;
	}
	
	.container {
		max-width: var(--container-max);
		margin-inline: auto;
		padding-inline: var(--container-padding);
	}
	
	.sitemap-header {
		text-align: center;
		margin-bottom: var(--space-16);
	}
	
	.sitemap-title {
		font-size: var(--font-size-4xl);
		font-weight: var(--font-weight-bold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
	}
	
	.sitemap-description {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		max-width: 600px;
		margin-inline: auto;
	}
	
	.sitemap-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
		gap: var(--space-8);
		margin-bottom: var(--space-16);
	}
	
	.sitemap-section {
		background: var(--bg-secondary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-xl);
		padding: var(--space-6);
		transition: var(--transition-all);
	}
	
	.sitemap-section:hover {
		border-color: var(--border-medium);
		box-shadow: var(--shadow-md);
		transform: translateY(-2px);
	}
	
	.section-header {
		display: flex;
		align-items: center;
		gap: var(--space-3);
		margin-bottom: var(--space-4);
		padding-bottom: var(--space-4);
		border-bottom: 1px solid var(--border-light);
	}
	
	.section-icon {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 36px;
		height: 36px;
		background: var(--bg-tertiary);
		color: var(--text-brand);
		border-radius: var(--radius-md);
	}
	
	.section-title {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
	}
	
	.section-links {
		list-style: none;
		padding: 0;
		margin: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-3);
	}
	
	.link-item {
		display: flex;
		flex-direction: column;
		gap: var(--space-1);
	}
	
	.link-url {
		display: inline-flex;
		align-items: center;
		gap: var(--space-1);
		color: var(--text-primary);
		font-size: var(--font-size-base);
		font-weight: var(--font-weight-medium);
		text-decoration: none;
		transition: var(--transition-colors);
	}
	
	.link-url:hover {
		color: var(--text-brand);
	}
	
	.link-url :global(svg) {
		opacity: 0.5;
		transition: var(--transition-opacity);
	}
	
	.link-url:hover :global(svg) {
		opacity: 1;
	}
	
	.link-description {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		line-height: var(--line-height-relaxed);
	}
	
	.sitemap-footer {
		text-align: center;
		padding-top: var(--space-12);
		border-top: 1px solid var(--border-light);
	}
	
	.xml-sitemap {
		display: inline-flex;
		align-items: center;
		gap: var(--space-2);
		padding: var(--space-4) var(--space-6);
		background: var(--bg-secondary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-lg);
		color: var(--text-secondary);
		font-size: var(--font-size-sm);
	}
	
	.xml-sitemap a {
		color: var(--text-brand);
		text-decoration: underline;
		text-underline-offset: var(--text-underline-offset);
		transition: var(--transition-colors);
	}
	
	.xml-sitemap a:hover {
		color: var(--brand-dark);
	}
	
	/* Responsive */
	@media (max-width: 768px) {
		.sitemap-title {
			font-size: var(--font-size-3xl);
		}
		
		.sitemap-grid {
			grid-template-columns: 1fr;
		}
	}
</style>